<template>
  <v-container fluid class="home-page py-6 px-4">
    <v-row>
      <!-- Zona principal: cumpleaños de hoy (grande) -->
      <v-col cols="12" md="8" lg="9">
        <v-card class="cumpleanos-hoy-card" elevation="2">
          <v-card-title class="text-h5 font-weight-bold py-4">
            <v-icon left color="primary" large>mdi-cake-variant</v-icon>
            Cumpleaños de hoy
          </v-card-title>
          <v-card-text class="pt-0">
            <div v-if="loading" class="text-center py-12">
              <v-progress-circular indeterminate color="primary" size="48" />
              <p class="mt-3 text--secondary">Cargando...</p>
            </div>
            <div v-else-if="error" class="text-center py-8">
              <v-alert type="warning" class="ma-4" dense outlined>{{ error }}</v-alert>
              <v-btn color="primary" outlined @click="cargar">Reintentar</v-btn>
            </div>
            <div v-else-if="hoy.length === 0" class="text-center py-12 cumpleanos-empty">
              <v-icon size="80" color="grey lighten-1">mdi-cake</v-icon>
              <p class="text-h6 mt-4 text--secondary">Nadie cumple años hoy</p>
              <p class="text-body-2 text--secondary">Los próximos cumpleaños aparecen al lado.</p>
            </div>
            <div v-else class="cumpleanos-hoy-list">
              <div
                v-for="c in hoy"
                :key="c.id"
                class="cumpleanos-hoy-item"
              >
                <v-avatar size="64" color="primary" class="mr-4 avatar-iniciales">
                  <span class="white--text text-h5">{{ iniciales(c) }}</span>
                </v-avatar>
                <div>
                  <div class="text-h4 font-weight-bold nombre-cumpleanero">
                    {{ nombreCompleto(c) }}
                  </div>
                  <div v-if="c.area" class="text-body-2 text--secondary mt-1">{{ c.area }}</div>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Sidebar derecha: próximos 2 meses -->
      <v-col cols="12" md="4" lg="3">
        <v-card class="cumpleanos-proximos-card" elevation="2">
          <v-card-title class="text-subtitle-1 font-weight-bold py-3">
            <v-icon left small>mdi-calendar-month</v-icon>
            Próximos 2 meses
          </v-card-title>
          <v-divider />
          <v-card-text class="pt-3">
            <div v-if="loading" class="text-center py-6">
              <v-progress-circular indeterminate color="primary" size="32" />
            </div>
            <div v-else-if="proximos2Meses.length === 0" class="text-center py-6 text--secondary text-body-2">
              No hay cumpleaños en los próximos 2 meses.
            </div>
            <v-list v-else dense class="py-0">
              <v-list-item
                v-for="c in proximos2Meses"
                :key="`${c.id}-${c.next_date}`"
                class="px-0"
              >
                <v-list-item-avatar size="36" color="primary lighten-2" class="avatar-iniciales">
                  <span class="primary--text text-caption font-weight-bold">{{ iniciales(c) }}</span>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="text-body-2 font-weight-medium">
                    {{ nombreCompleto(c) }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-caption">
                    {{ formatearFechaProximo(c.next_date) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from "axios";
import API_BASE from "@/helpers/api";

export default {
  name: "HomeView",
  data() {
    return {
      loading: true,
      error: null,
      hoy: [],
      proximos2Meses: [],
    };
  },
  mounted() {
    this.cargar();
  },
  methods: {
    async cargar() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get(`${API_BASE}/cumpleanos`);
        this.hoy = data.hoy || [];
        this.proximos2Meses = data.proximos2Meses || [];
      } catch (e) {
        console.error("Error al cargar cumpleaños:", e);
        this.hoy = [];
        this.proximos2Meses = [];
        const status = e.response && e.response.status;
        this.error = status === 404
          ? "El servicio de cumpleaños no está disponible. Verifica que el backend esté actualizado y desplegado."
          : "No se pudieron cargar los cumpleaños. Revisa la conexión e intenta de nuevo.";
      } finally {
        this.loading = false;
      }
    },
    nombreCompleto(c) {
      return [c.name, c.surname].filter(Boolean).join(" ") || "Sin nombre";
    },
    iniciales(c) {
      const n = (c.name || "").trim();
      const s = (c.surname || "").trim();
      const a = n ? n[0].toUpperCase() : "";
      const b = s ? s[0].toUpperCase() : n.length > 1 ? n[1].toUpperCase() : "";
      return (a + b) || "?";
    },
    formatearFechaProximo(ymd) {
      if (!ymd) return "";
      const d = new Date(ymd + "T12:00:00");
      const opts = { day: "numeric", month: "long" };
      return d.toLocaleDateString("es-CL", opts);
    },
  },
};
</script>

<style scoped>
.home-page {
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
}

.cumpleanos-hoy-card {
  min-height: 320px;
}

.cumpleanos-hoy-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.cumpleanos-hoy-item {
  display: flex;
  align-items: center;
  padding: 16px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.02);
}

.theme--dark .cumpleanos-hoy-item {
  background: rgba(255, 255, 255, 0.06);
}

.nombre-cumpleanero {
  line-height: 1.2;
}

.cumpleanos-empty {
  color: inherit;
}

.cumpleanos-proximos-card {
  position: sticky;
  top: 80px;
}

/* Centrar iniciales dentro del avatar (hoy y próximos 2 meses) */
.home-page .avatar-iniciales {
  display: flex;
  align-items: center;
  justify-content: center;
}
.home-page .avatar-iniciales span {
  line-height: 1;
  display: block;
}
</style>
